/**
 * Category filter — an anchored dropdown menu.
 *
 * Same control on phone and desktop, which is what professional catalogue UIs
 * do (Linear, Shopify admin, GitHub's filter menus): a compact trigger showing
 * the active value, opening a vertical list anchored beneath it.
 *
 * Deliberately NOT a bottom sheet on mobile: a sheet is for a task, whereas
 * this is a filter that sits beside other filters and should read as one of
 * them. One implementation also means the two platforms cannot drift apart.
 *
 * The list scrolls vertically only, and contains its overscroll so reaching the
 * bottom never scrolls the page behind it.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

interface Props {
  /**
   * `count` is how many products the CATALOGUE holds in this category.
   * `eligible` is how many the CURRENTLY SELECTED criteria can actually rank
   * there, or null when that is not known.
   *
   * The two differ enormously and the second is the one the reader is asking
   * about: "highest protein" ranks 88 of 1,165 vegetables, and none of the 494
   * Lifestyle products, because Lifestyle is household goods with no nutrition.
   * Showing only the catalogue figure made a correct empty result look broken.
   */
  categories: Array<{ value: string; count: number; eligible?: number | null }>;
  selected: string | null;
  onChange: (value: string | null) => void;
}

/** Above this many options a filter box saves more time than it costs. */
const FILTER_THRESHOLD = 8;

export function CategoryPicker({ categories, selected, onChange }: Props): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointer = (e: PointerEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    // Focusing the filter lets a keyboard user type immediately; on touch the
    // menu is short enough that we skip it to avoid summoning the keyboard.
    if (categories.length > FILTER_THRESHOLD && !('ontouchstart' in window)) {
      inputRef.current?.focus();
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open, categories.length]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q.length === 0
      ? categories
      : categories.filter((c) => c.value.toLowerCase().includes(q));
  }, [categories, query]);

  /*
   * A category is unusable when the selected criteria can rank nothing in it.
   * Unknown (`eligible` absent or null) is never treated as zero -- an option
   * is only disabled on evidence, never on a missing number.
   */
  const isDead = (c: { eligible?: number | null }): boolean =>
    c.eligible !== undefined && c.eligible !== null && c.eligible === 0;

  const choose = (value: string | null): void => {
    onChange(value);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="catpick" ref={wrapRef}>
      <button
        type="button"
        className={`catpick-trigger${selected !== null ? ' is-set' : ''}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="catpick-icon" aria-hidden="true">
          ☰
        </span>
        <span className="catpick-label">{selected ?? 'All categories'}</span>
        <span className="catpick-caret" aria-hidden="true" />
      </button>

      {open && (
        <div className="catpick-menu" role="listbox" aria-label="Category">
          {categories.length > FILTER_THRESHOLD && (
            <div className="catpick-filter">
              <input
                ref={inputRef}
                type="text"
                value={query}
                placeholder="Filter…"
                aria-label="Filter categories"
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          )}

          <div className="catpick-list">
            <button
              type="button"
              role="option"
              aria-selected={selected === null}
              className={`catpick-opt${selected === null ? ' is-on' : ''}`}
              onClick={() => choose(null)}
            >
              <span className="catpick-tick" aria-hidden="true">
                {selected === null ? '✓' : ''}
              </span>
              <span className="catpick-opt-name">All categories</span>
            </button>

            {visible.map((c) => {
              const dead = isDead(c);
              const shown = c.eligible !== undefined && c.eligible !== null ? c.eligible : c.count;
              return (
                <button
                  key={c.value}
                  type="button"
                  role="option"
                  aria-selected={selected === c.value}
                  aria-disabled={dead}
                  disabled={dead}
                  title={
                    dead
                      ? `No product in ${c.value} has what these criteria need.`
                      : `${shown.toLocaleString()} rankable with the current criteria`
                  }
                  className={
                    `catpick-opt${selected === c.value ? ' is-on' : ''}${dead ? ' is-dead' : ''}`
                  }
                  onClick={() => { if (!dead) choose(c.value); }}
                >
                  <span className="catpick-tick" aria-hidden="true">
                    {selected === c.value ? '✓' : ''}
                  </span>
                  <span className="catpick-opt-name">{c.value}</span>
                  <span className="catpick-opt-count">
                    {dead ? 'none' : shown.toLocaleString()}
                  </span>
                </button>
              );
            })}

            {visible.length === 0 && <p className="catpick-none">No category matches “{query}”.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
